import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Mark } from '../components/Layout';
import { ErrorNote, Field, LanguageSwitch } from '../components/ui';
import { get, post } from '../lib/api';
import { useAuth } from '../lib/auth';

interface SetupInfo {
  fullName: string;
  email: string | null;
  schoolName: string;
  schoolNameAr: string | null;
}

/** "Set your password" from a link the platform team (or a reset) sent */
export function Setup() {
  const { token = '' } = useParams();
  const { t, i18n } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState<SetupInfo | null | 'invalid'>(null);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [doneSuspended, setDoneSuspended] = useState(false);

  useEffect(() => {
    get<SetupInfo>(`/api/auth/setup/${encodeURIComponent(token)}`)
      .then(setInfo)
      .catch(() => setInfo('invalid'));
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (pw !== pw2) return setError({ message: t('setup.mismatch') });
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ user: unknown }>(`/api/auth/setup/${encodeURIComponent(token)}`, { password: pw, locale: i18n.language });
      if (!r.user) return setDoneSuspended(true);
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const school = (info && info !== 'invalid' ? (i18n.language === 'ar' && info.schoolNameAr) || info.schoolName : '').replace(/\.+$/, '');
  return (
    <div className="login">
      <div className="login-lang">
        <LanguageSwitch />
      </div>
      <section className="login-card">
        <div className="login-brand">
          <Mark />
          <h1>{t('setup.title')}</h1>
        </div>
        {info === null && <p className="muted">{t('common.loading')}</p>}
        {info === 'invalid' && <p className="error-note">{t('setup.invalid')}</p>}
        {doneSuspended && <p className="muted">{t('setup.doneSuspended')}</p>}
        {info && info !== 'invalid' && !doneSuspended && (
          <form className="stack" onSubmit={submit}>
            <p className="login-lede">
              <strong>{t('setup.welcome', { name: info.fullName })}</strong>
              <br />
              {t('setup.forSchool', { school })}
            </p>
            {info.email && <input type="email" autoComplete="username" value={info.email} readOnly hidden />}
            <Field label={t('setup.password')} hint={t('setup.hint')}>
              <input type="password" autoComplete="new-password" minLength={10} required value={pw} onChange={(e) => setPw(e.target.value)} dir="ltr" />
            </Field>
            <Field label={t('setup.repeat')}>
              <input type="password" autoComplete="new-password" minLength={10} required value={pw2} onChange={(e) => setPw2(e.target.value)} dir="ltr" />
            </Field>
            <ErrorNote error={error} />
            <button className="btn btn-primary btn-block" disabled={busy}>
              {t('setup.save')}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
